import React, { useState, useEffect, useCallback } from "react";
import apiClient, { getApiError } from "../apiClient";

/**
 * Parcha Expense — mini expense manager for the Daily Closing card.
 *
 * Reuses the existing Expense module end to end. No new table, no new
 * storage, no syncing:
 *
 *   heads  →  POST /Liquor/GetExpenseMaster      (same as /expensedate)
 *   add    →  POST /Liquor/SaveExpenseWithDate   (same as /expensedate)
 *   list   →  POST /Liquor/GetExpenseReport      (same as Expense Report)
 *
 * Everything written here lands in TrnExpense, so it shows up in
 * /salesreport, Expense Report, Cash Book and P&L with no extra code.
 *
 * Props
 *   shopId,userId,companyId : identity for the expense rows
 *   date                    : yyyy-MM-dd the parcha is being entered for
 *   onTotalChange           : called with the day's expense total
 *   showToast               : host page's toast function
 *   accent                  : "en" | "desi" — colour only, no behaviour change
 */
const ParchaExpense = ({ shopId, userId, companyId, date, onTotalChange, showToast, accent = "en" }) => {
  const [heads,    setHeads]    = useState([]);
  const [headId,   setHeadId]   = useState("");
  const [amount,   setAmount]   = useState("");
  const [rows,     setRows]     = useState([]);
  const [busy,     setBusy]     = useState(false);
  const [loading,  setLoading]  = useState(false);

  const fmt = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  /* TrnExpense has no column recording which screen created an expense, so
     rows added here are tagged in Remark and the list filters on that tag.
     Without it the panel also showed expenses entered through /expensedate
     for the same shop and date. */
  const TAG = "Parcha: ";
  const isParchaRow = (r) => String(r.remark ?? r.Remark ?? "").startsWith(TAG);

  /* sp_GetExpense returns ExpenseDate already formatted as 'dd-MMM-yyyy',
     so the day filter compares in that same shape rather than parsing. */
  const asDisplayDate = (iso) => {
    const d = iso ? new Date(iso) : new Date();
    if (isNaN(d)) return "";
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${String(d.getDate()).padStart(2, "0")}-${months[d.getMonth()]}-${d.getFullYear()}`;
  };

  /* ── Expense heads: the existing master, not a new one ── */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apiClient.post("/Liquor/GetExpenseMaster", { companyId });
        const list = res?.data?.data || res?.data?.Data || [];
        if (alive) setHeads(Array.isArray(list) ? list.filter((h) => h.isActive !== false) : []);
      } catch {
        if (alive) setHeads([]);
      }
    })();
    return () => { alive = false; };
  }, [companyId]);

  /* ── Today's expenses for this shop ── */
  const loadRows = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const res = await apiClient.post("/Liquor/GetExpenseReport", { shopId });
      const all = res?.data?.data || res?.data?.Data || [];
      const want = asDisplayDate(date);
      const mine = (Array.isArray(all) ? all : [])
        .filter((r) => (r.expenseDate || r.ExpenseDate) === want)
        .filter(isParchaRow);
      setRows(mine);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [shopId, date]);

  useEffect(() => { loadRows(); }, [loadRows]);

  /* ── Keep the host page's Net Cash in step ── */
  const total = rows.reduce((s, r) => s + (Number(r.amount ?? r.Amount) || 0), 0);
  useEffect(() => { if (onTotalChange) onTotalChange(total); }, [total, onTotalChange]);

  /* ── Add: same endpoint and same payload shape as /expensedate ── */
  const addExpense = async () => {
    const amt = parseFloat(amount);
    if (!headId)          { showToast?.("Please choose an expense head.", "error"); return; }
    if (!amt || amt <= 0) { showToast?.("Enter an amount greater than zero.", "error"); return; }

    /* sp_SaveExpenseWithDate builds a temp table where ShopId, UserId, Amount,
       Remark and ExpenseDate are all NOT NULL, so a missing identity makes the
       whole insert throw. Catch it here with a message that says what is wrong
       rather than letting it fail inside the procedure. */
    const uid = parseInt(userId, 10);
    const sid = parseInt(shopId, 10);
    if (!sid) { showToast?.("No shop selected — please reload and try again.", "error"); return; }
    if (!uid) { showToast?.("Your session has no user id — please sign in again.", "error"); return; }

    const head = heads.find((h) => String(h.id ?? h.Id) === String(headId));
    const body = [{
      shopId: sid,
      expenseId: parseInt(headId, 10),
      expenseAmount: amt,
      userId: uid,
      Remark: TAG + (head?.expName || head?.ExpName || "Expense"),
      expenseDate: date || new Date().toISOString().split("T")[0],
    }];

    setBusy(true);
    try {
      const res = await apiClient.post("/Liquor/SaveExpenseWithDate", body);
      /* The API answers HTTP 200 even when it failed — the real result is in
         data.code. Treating res.status as success reported "added" for saves
         that never reached the database. */
      const ok = res?.data?.code === 200;
      if (ok) {
        showToast?.("Expense added.", "success");
        setHeadId(""); setAmount("");
        await loadRows();
      } else {
        showToast?.(res?.data?.message || res?.data?.data || "Could not add the expense.", "error");
      }
    } catch (err) {
      showToast?.(getApiError(err, "Could not add the expense."), "error");
    } finally {
      setBusy(false);
    }
  };

  /* Removes one expense by its own TrnExpense.Id via sp_DeleteExpenseById,
     which also clears the matching TblLedger row and writes both to history.
     Deliberately not sp_DeleteExpensesByShop — that matches on the expense
     HEAD id with no date filter and would wipe every expense of that head. */
  const deleteExpense = async (row) => {
    const id = row.id ?? row.Id;
    const name = row.expName ?? row.ExpName ?? "this expense";
    if (!id) return;
    if (!window.confirm(`Delete ${name}?`)) return;

    setBusy(true);
    try {
      const res = await apiClient.post("/Liquor/DeleteExpense", {
        Id: id,
        DeletedBy: localStorage.getItem("userName") || "unknown",
      });
      if (res?.data?.code === 200) {
        showToast?.(res?.data?.data || "Expense deleted.", "success");
        await loadRows();
      } else {
        showToast?.(res?.data?.message || "Could not delete the expense.", "error");
      }
    } catch (err) {
      showToast?.(getApiError(err, "Could not delete the expense."), "error");
    } finally {
      setBusy(false);
    }
  };

  const onAmountKey = (e) => { if (e.key === "Enter") { e.preventDefault(); addExpense(); } };

  return (
    <div className={`pxp ${accent === "desi" ? "pxp-desi" : ""}`}>
      <div className="pxp-title">Parcha Expense</div>

      <div className="pxp-form">
        <div className="pxp-field pxp-field-head">
          <label htmlFor="pxpHead">Expense Head</label>
          <select id="pxpHead" className="pxp-sel" value={headId}
                  onChange={(e) => setHeadId(e.target.value)} disabled={busy}>
            <option value="">Select Expense</option>
            {heads.map((h) => {
              const id = h.id ?? h.Id;
              return <option key={id} value={id}>{h.expName ?? h.ExpName}</option>;
            })}
          </select>
        </div>

        <div className="pxp-field pxp-field-amt">
          <label htmlFor="pxpAmt">Amount</label>
          <input id="pxpAmt" type="number" min="0" className="pxp-inp" placeholder="0"
                 value={amount} onChange={(e) => setAmount(e.target.value)}
                 onKeyDown={onAmountKey} disabled={busy} />
        </div>

        <button type="button" className="pxp-add" onClick={addExpense} disabled={busy}>
          {busy ? "Adding…" : "+ Add Expense"}
        </button>
      </div>

      <div className="pxp-list">
        {loading && <div className="pxp-empty">Loading…</div>}

        {!loading && rows.length === 0 && (
          <div className="pxp-empty">No expense added for this date yet.</div>
        )}

        {!loading && rows.map((r) => (
          <div className="pxp-row" key={r.id ?? r.Id}>
            <span className="pxp-row-name">{r.expName ?? r.ExpName}</span>
            <span className="pxp-row-amt">{fmt(r.amount ?? r.Amount)}</span>
            <button type="button" className="pxp-del" disabled={busy}
                        onClick={() => deleteExpense(r)} title="Delete this expense">
              🗑
            </button>
          </div>
        ))}
      </div>

      <div className="pxp-total">
        <span className="pxp-total-lbl">Total Expense</span>
        <span className="pxp-total-val">{fmt(total)}</span>
      </div>
    </div>
  );
};

export default ParchaExpense;
